import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { BsBookmarkStarFill, BsHouseDoorFill } from "react-icons/bs";
import useFavorites from "../../hooks/useFavorites";

const Shell = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 30;
  transition: background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  border-bottom: 1px solid transparent;

  ${({ $solid, theme }) =>
    $solid
      ? css`
          background: ${theme.colors.glassStrong};
          backdrop-filter: blur(18px);
          border-color: ${theme.colors.border};
          box-shadow: ${theme.shadows.soft};
        `
      : css`
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.7), transparent);
        `}
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: min(1280px, calc(100% - 1.5rem));
  min-height: 76px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.65rem;
    width: min(1280px, calc(100% - 1rem));
    padding: 0.75rem 0;
  }
`;

const Brand = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: bold;
  color: ${({ theme }) => theme.colors.red};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
    line-height: 1;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    gap: 0.55rem;
  }
`;

const NavItem = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 700;
  white-space: nowrap;
  transition: color 0.2s ease, background 0.2s ease;

  &.active,
  &:hover {
    color: ${({ theme }) => theme.colors.white};
    background: rgba(255, 255, 255, 0.08);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 1 1 calc(50% - 0.275rem);
    justify-content: center;
    padding: 0.7rem 0.85rem;
    font-size: 0.92rem;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  padding: 0 0.35rem;
  border-radius: 50%;
  font-size: 0.78rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.red};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 22px;
    min-height: 22px;
    font-size: 0.72rem;
  }
`;

function Header() {
  const location = useLocation();
  const { favorites } = useFavorites();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 18);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const solidHeader = useMemo(
    () => isScrolled || location.pathname !== "/",
    [isScrolled, location.pathname]
  );

  return (
    <Shell $solid={solidHeader}>
      <Inner>
        <Brand to="/">NovaFlix</Brand>
        <Nav aria-label="Navegacao principal">
          <NavItem to="/" end>
            <BsHouseDoorFill aria-hidden />
            Inicio
          </NavItem>
          <NavItem to="/favoritos">
            <BsBookmarkStarFill aria-hidden />
            Minha Lista
            <Badge aria-label={`${favorites.length} filmes salvos`}>{favorites.length}</Badge>
          </NavItem>
        </Nav>
      </Inner>
    </Shell>
  );
}

export default Header;
